import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import { OrderType } from '../../enums/OrderType';
import { Customer } from '../../models/Customer';
import { FormatHelper } from '../../helpers/FormatHelper';
import { FilterCustomerInput } from '../../services/Customers/FilterCustomer/FilterCustomerInput';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { If } from '../../common-components/If/If';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';
import { WaitingBox } from '../../common-components/WaitingBox/WaitingBox';
import { BadgeTitled } from '../../common-components/BadgeTitled/BadgeTitled';
import {
    Row, CustomerName, Values, ItalicText, UnderlineText, ConfirmationText,
    FormFilters, ListCustomers, Title, Subtitle,
    LeftButtons, RightButtons, SpacedButtons
} from './styles';
import { ChangeOrderCustomerInput } from '../../services/Orders/ChangeOrderCustomer/ChangeOrderCustomerInput';

interface ModalChangeOrderCustomerProps {
    visible: boolean;
    orderId?: number;
    orderNumber?: string;
    close: (changed?: boolean) => void;
}

const ModalChangeOrderCustomer: React.FC<ModalChangeOrderCustomerProps> = ({
    visible,
    orderId,
    orderNumber,
    close,
}) => {

    const { session } = useAuth();
    const [ selectedCustomer, setSelectedCustomer ] = React.useState<Customer|null|undefined>(undefined);
    const [ listCustomer, setListCustomer ] = React.useState<Customer[]>([]);
    const [ searching, setSearching ] = React.useState<boolean>(false);
    const [ document, setDocument ] = React.useState<string|undefined>(undefined);
    const [ name, setName ] = React.useState<string|undefined>(undefined);
    const [ email, setEmail ] = React.useState<string|undefined>(undefined);
    const [ phone, setPhone ] = React.useState<string|undefined>(undefined);

    const {
        filterCustomerService,
        toastService,
        changeOrderCustomerService
    } = React.useContext(ServicesContext);

    const title = React.useMemo(() => { 
        if (!visible) return null;

        if (searching) return <span>Localizando...</span>;

        if (selectedCustomer) return <span>Por fim, confirme a alteração</span>;

        if (listCustomer.length > 0) return <span>Agora selecione o cliente desejado</span>;

        return <span>Primeiro localize o cliente</span>;
    }, [ visible, selectedCustomer, listCustomer, searching,  document, phone, name, email ]);

    const body = React.useMemo(() => {
        if (!visible) return null;

        if (searching) {
            return (
                <WaitingBox />
            );
        };

        if (selectedCustomer) {
            return (
                <ConfirmationText>
                    Ao confirmar a alteração o cliente do pedido <ItalicText>{orderNumber}</ItalicText> será alterado para <UnderlineText>{selectedCustomer.firstName} {selectedCustomer.lastName}</UnderlineText>.
                </ConfirmationText>
            );
        }

        if (listCustomer.length === 0) {
            return (
                <FormFilters>
                    <Row>
                        <Field
                            type={FieldType.Text}
                            label={'CPF/CNPJ'}
                            marginRight='5px'
                            value={document}
                            change={(value) => setDocument(value)}
                            replaceEnterCodeByValue={' '}
                            autoFocus={true}
                            marginTop={'0px'}
                            maxLength={20}
                        />
                        <Field
                            type={FieldType.Text}
                            label={strings.Phone}
                            value={phone}
                            change={(value) => setPhone(value)}
                            marginTop={'0px'}
                            maxLength={20}
                        />
                    </Row>
                    <Row>
                        <Field
                            type={FieldType.Text}
                            label={strings.Name}
                            value={name}
                            change={(value) => setName(value)}
                            replaceEnterCodeByValue={' '}
                            marginTop={'0px'}
                        />
                    </Row>
                    <Row>
                        <Field
                            type={FieldType.Text}
                            label={strings.Email}
                            value={email}
                            change={(value) => setEmail(value)}
                            marginTop={'0px'}
                        />
                    </Row>
                </FormFilters>
            );
        }

        return (
            <ListCustomers>
                {listCustomer.map((customer, index) => {
                    return (
                        <Values
                            key={`customer-${customer.id}`}
                            onClick={() => setSelectedCustomer(customer)}
                        >
                            <CustomerName>{customer.firstName} {customer.lastName}</CustomerName>
                            <If condition={!!customer.email}>
                                <BadgeTitled text={customer.email} />
                            </If>
                            <If condition={!!customer.phone}>
                                <BadgeTitled title='Tel' text={customer.phone} />
                            </If>
                            <If condition={!!customer.document}>
                                <BadgeTitled title='Doc' text={FormatHelper.formatPersonalIdentificationNumber(customer.document)} />
                            </If>
                        </Values>
                    );
                })}
            </ListCustomers>
        );
    }, [ visible, selectedCustomer, listCustomer, searching,  document, phone, name, email ]);

    const footer = React.useMemo(() => {
        if (!visible || searching) return null;

        if (selectedCustomer) return (
            <SpacedButtons>
                <Button text={strings.Return} click={() => setSelectedCustomer(null)} />
                <Button text={strings.Confirm} click={changeCustomer} />
            </SpacedButtons>
        );

        if (listCustomer.length === 0) return (
            <RightButtons>
                <Button text={strings.Search} click={searchCustomer} />
            </RightButtons>
        );

        return (
            <LeftButtons>
                <Button text={strings.Return} click={() => setListCustomer([])} />
            </LeftButtons>
        );
    }, [ visible, selectedCustomer, listCustomer, searching,  document, phone, name, email ]);

    if (!visible) return null;

    return (
        <Modal
            show={visible}
            onHide={() => clickCloseModal()}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton style={{height:'50px'}}>
                <Modal.Title style={{display:'flex', flexDirection:'column'}}>
                    <Title>{strings.ChangeCustomer}</Title>
                    <Subtitle>{title}</Subtitle>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{maxHeight:'70vh', overflowY:'auto'}}>
                {body}
            </Modal.Body>

            <Modal.Footer style={{display:'flex', alignItems:'center', justifyContent:'center'}}>
                {footer}
            </Modal.Footer>
        </Modal>
    );

    function clickCloseModal() {
        setSearching(false);
        setSelectedCustomer(null);
        setListCustomer([]);
        setDocument(undefined);
        setPhone(undefined);
        setName(undefined);
        setEmail(undefined);
        close(true);
    }

    function searchCustomer() {
        console.log(`searching by ${document} ${phone} ${name} ${email}...`);

        if (!document && !name && !email && !phone) {
            toastService.warning('Informe pelo menos um filtro!');
            return;
        }

        const filterInput = new FilterCustomerInput();
        filterInput.pageSize = 1;
        filterInput.currentPage = 1;
        filterInput.shouldPaginate = false;
        filterInput.orderBy = 'CreatedAt';
        filterInput.orderType = OrderType.Descending;
        if (name) {
            filterInput.orderBy = 'Name';
            filterInput.orderType = OrderType.Ascending;
        }
        filterInput.document = document;
        filterInput.name = name;
        filterInput.email = email;
        filterInput.phone = phone;

        setSearching(true);

        filterCustomerService.handle(filterInput)
            .then(result => {
                const list = result?.value?.list || [];
                setListCustomer(list);
                if (list.length === 0) {
                    toastService.info('Nenhum cliente encontrado para os filtros informados!');
                }
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongGettingTheData);
            })
            .finally(() => {
                setSearching(false);
            });
    }

    function changeCustomer() {
        if (!session?.user?.id) {
            toastService.warning('Precisa estar logado para realizar esta alteração!');
            return;
        }

        if (!selectedCustomer) {
            toastService.warning('Selecione o cliente desejado antes de realizar a alteração!');
            return;
        }

        const input = new ChangeOrderCustomerInput();
        input.orderId = orderId;
        input.customerId = selectedCustomer.id;
        input.userId = session.user.id;
        changeOrderCustomerService.handle(input)
            .then(result => {
                if (result?.value?.result) {
                    clickCloseModal();
                    return;
                }
                toastService.warning('Não foi possível realizar a alteração. Revise os dados informados e tente novamente.');
            })
            .catch(reason => {
                toastService.warning(strings.OpsSomethingWrongSendTheData);
            });
    }
}

export {ModalChangeOrderCustomer};